// ui/presenters/billingItemRowPresenter.js
import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';

import { formatTimeInZone } from 'src/kernel/time';
import { formatCalendarDate } from 'src/utils/DateUtils';

import { currencyAffix, fromMinorUnits } from '../../domain/constants/currency';
import { primaryHoldReason } from '../../domain/services/holdReasonAudience';

/**
 * Presenter: turns a projected billing item into the row the worklist renders.
 *
 * IT IS TOLD THE NAMES rather than looking them up. An item stores a client, a payer and a provider
 * as references and deliberately snapshots none of their names, so something has to resolve them —
 * and a presenter that reached into stores to do it would stop being a pure mapping and start being
 * untestable. The caller passes what it already holds.
 *
 * A NAME THAT CANNOT BE RESOLVED IS NOT AN EMPTY CELL. Falling back to the id shows something a
 * person can search for and report; blank reads as "this item has no payer", which is a different
 * and much more alarming claim.
 *
 * SORTING, DISPLAY AND FILTERING ARE THREE FIELDS on purpose, and each is a different type because
 * each consumer demands one. The table sorts on the value it is given, so a formatted date sorts
 * alphabetically — 'Apr' before 'Jan' — and the raw calendar date is the sortable one precisely
 * because zero-padded dates sort chronologically as strings. The date FILTER is stricter still: it
 * refuses anything that is not a Firestore Timestamp outright, so a calendar date silently matches
 * nothing at all rather than comparing wrongly.
 */

/**
 * The service date as an instant, for the one consumer that cannot read a calendar date.
 *
 * BUILT FROM LOCAL PARTS, NEVER PARSED. `new Date('2026-09-04')` is UTC midnight, which in any
 * negative-offset timezone is the 3rd locally — so a service date would land in the wrong day and a
 * biller filtering to a period would silently miss its edges. The filter's own bounds are local, so
 * this has to be local midnight of the same calendar day.
 */
function toLocalInstant(calendarDate) {
  if (typeof calendarDate !== 'string' || calendarDate.length < 10) {
    return null;
  }
  const [year, month, day] = calendarDate.slice(0, 10).split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }
  return Timestamp.fromDate(new Date(year, month - 1, day));
} // toLocalInstant()

/**
 * Billable time in minutes, as a NUMBER.
 *
 * THE ITEM CARRIES NO UNITS ON PURPOSE — a unit is decided at the claim, on time pooled across
 * overlapping delivery — but a biller still has to arrive at one, and an invoice is billed on time
 * directly. So this is the quantity they divide.
 *
 * MINUTES RATHER THAN m:ss, AND THAT IS ABOUT THE SPREADSHEET. "61:00" in a cell is parsed by Excel
 * as a time — sixty-one HOURS — and a biller dividing it by fifteen gets a plausible, wrong answer.
 * A number cannot be reinterpreted, and `=B2/15` is the whole conversion.
 *
 * TWO DECIMALS, so a short activity does not round away to zero. Rounding at all is deferred to the
 * claim, where the payer's rule decides it; stating 0.27 says what happened without pretending to
 * know what it bills.
 */
function presentDurationMinutes(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  return Math.round((seconds / 60) * 100) / 100;
} // presentDurationMinutes()

/**
 * A time of day, read in the zone the item says its times were decided in.
 *
 * THE ITEM'S ZONE, NEVER THE VIEWER'S. The window was split at midnight in that zone and the
 * service date was decided in it, so reading an instant anywhere else can print an hour that
 * contradicts the date in the next column — a session at 21:00 on the 5th shown as 05:00 on the
 * 6th to somebody in Copenhagen.
 *
 * WITHOUT A ZONE THERE IS NO TIME OF DAY TO SHOW. An item projected before the stamp existed, or
 * for a clinic with none set, says nothing rather than guessing: a wrong hour reads as fact, while
 * an empty cell reads as missing.
 */
function presentTimeOfDay(instant, timeZone) {
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime()) || !timeZone) {
    return null;
  }
  try {
    return formatTimeInZone(instant, timeZone, {
      options: { hour: '2-digit', minute: '2-digit', hour12: false },
    });
  } catch (error) {
    // An unknown zone throws rather than silently falling back to the viewer's, which is the
    // outcome this whole function exists to avoid.
    return null;
  }
} // presentTimeOfDay()

/**
 * The same money as a NUMBER, for the file rather than the screen.
 *
 * A biller multiplies it by the units they worked out and sums the column; "€10.00" is text a
 * spreadsheet can do neither with.
 *
 * `fromMinorUnits` RETURNS A STRING ON PURPOSE — exact decimal, no float — and this converts anyway,
 * because a spreadsheet cell has to hold a number to be arithmetic. The exact form is what the
 * screen shows, so nothing is lost where precision is what matters.
 */
function chargeAsNumber(chargePerUnit) {
  const amount = fromMinorUnits(chargePerUnit?.amountMinor, chargePerUnit?.currency);

  return amount === null ? null : Number(amount);
} // chargeAsNumber()

/** A charge with its currency, or a dash. Per unit, which the column header has to say. */
function presentCharge(chargePerUnit) {
  const amount = fromMinorUnits(chargePerUnit?.amountMinor, chargePerUnit?.currency);

  if (amount === null) {
    return null;
  }
  const affix = currencyAffix(chargePerUnit.currency);

  return affix === null ? amount : `${affix}${amount}`;
} // presentCharge()

/**
 * The code as a biller reads it: the code, then whatever modifies it.
 *
 * Modifiers are part of what is billed rather than decoration — the same code with a different
 * modifier is a different line to a payer — so they belong beside it rather than in a column
 * somebody can hide.
 */
function presentCode(item) {
  if (!item.billingCode) {
    return null;
  }
  return item.modifiers.length > 0
    ? `${item.billingCode} ${item.modifiers.join(' ')}`
    : item.billingCode;
} // presentCode()

/**
 * Whoever an item points at, by the reference it stores when the caller cannot name them.
 *
 * NAMES ONLY. The id fallback is what makes an unresolved row searchable rather than alarming, and
 * it only reads that way for something that could have been a name.
 *
 * @param {Function} [resolve] - the caller's lookup for this kind of reference
 * @param {string} [id] - the reference the item stores
 * @returns {string|null}
 */
function name(resolve, id) {
  if (!id) {
    return null;
  }
  return resolve?.(id) || id;
} // name()

/**
 * A hold in a biller's words. An authorization names its payer, and one recorded against another
 * payer clears nothing here, so the hold says which payer it needs one from. Without a resolved name
 * it reads as the reason alone, since an id in a sentence reads as a corrupt value.
 */
function presentHoldReason(holdReason, payerName) {
  if (!holdReason) {
    return null;
  }
  if (holdReason === 'held_for_auth' && payerName) {
    return i18n.t('billingItems.textNoAuthorizationFromPayer', { payer: payerName });
  }
  return i18n.t(`billingItems.holdReason.${holdReason}`);
} // presentHoldReason()

/**
 * @param {import('../../domain/model/BillingItem.types').BillingItem} item
 * @param {{clientName?: Function, clientDateOfBirth?: Function, payerName?: Function,
 *   providerName?: Function}} [lookups]
 * @returns {Object} the table row
 */
// eslint-disable-next-line import/prefer-default-export
export function presentBillingItemRow(item, lookups = {}) {
  const holdReason = primaryHoldReason(item.holdReasons);
  const clientDateOfBirth = lookups.clientDateOfBirth?.(item.clientId) ?? null;

  return {
    id: item.id,
    sessionActivityId: item.sessionActivityId,
    serviceDate: item.serviceDate,
    serviceDateLabel: item.serviceDate ? formatCalendarDate(item.serviceDate) : null,
    serviceDateAt: toLocalInstant(item.serviceDate),
    clientName: name(lookups.clientName, item.clientId),
    payerName: name(lookups.payerName, item.fundingSourceId),
    code: presentCode(item),
    // Beside the code as well as inside it: a payer portal takes them in their own fields, and the
    // combined form is what a biller reads on screen.
    modifiers: item.modifiers.length > 0 ? item.modifiers.join(' ') : null,
    // AS STORED AND IN ORDER. A claim points each service line at a diagnosis, and the first is the
    // primary one, so the order is part of the answer rather than a display choice.
    diagnosisCodes: item.diagnosisCodes.length > 0 ? item.diagnosisCodes.join(', ') : null,
    charge: presentCharge(item.chargePerUnit),
    chargeAmount: chargeAsNumber(item.chargePerUnit),
    chargeCurrency: item.chargePerUnit?.currency ?? null,
    placeOfServiceCode: item.placeOfServiceCode,
    billableDuration: item.billableDuration,
    serviceStartLabel: presentTimeOfDay(item.serviceStart, item.serviceTimezone),
    serviceEndLabel: presentTimeOfDay(item.serviceEnd, item.serviceTimezone),
    serviceTimezone: item.serviceTimezone,
    memberId: item.memberId,
    // Every match, because which one a claim counts against is not decided here.
    authorizationNumbers: item.authorizations.length > 0
      ? item.authorizations
        .map(({ authorizationNumber }) => authorizationNumber ?? i18n.t('billingItems.textAuthorizationNumberPending'))
        .join(', ')
      : null,
    // 'self' means the patient is the subscriber; anything else is a person the biller must find.
    policyholderRelationship: item.policyholderRelationship,
    durationMinutes: presentDurationMinutes(item.billableDuration),
    // The number itself, not just who renders. A claim names the NPI; the screen names the person.
    renderingProviderNpi: item.renderingProviderNpi,
    // THE CALENDAR DATE AND THE FORMATTED ONE, as the service date above, and NOT through the name
    // resolver: an unresolved date has to be an empty cell, because an id in a date column reads as
    // a corrupt value rather than a missing one.
    clientDateOfBirth,
    clientDateOfBirthLabel: formatCalendarDate(clientDateOfBirth),
    // The organization bills under its own NPI and the individual renderer is null ON PURPOSE, so
    // an empty cell here would read as a missing provider rather than a deliberate one.
    renderingProvider: item.billedByGroup
      ? i18n.t('billingItems.renderingProvider.organization')
      : name(lookups.providerName, item.renderingProviderId),
    // BOTH THE VALUE AND THE WORDS. The filter narrows on the status a projection recorded; the
    // column shows the reason instead, because "held" tells a biller nothing they can act on.
    status: item.status,
    isHeld: item.status === 'held',
    holdReason,
    holdReasonLabel: presentHoldReason(holdReason, lookups.payerName?.(item.fundingSourceId)),
    exportState: item.exportState,
    exportStateLabel: i18n.t(`billingItems.exportState.${item.exportState}`),
    lastExportTimestamp: item.lastExportTimestamp,
  };
} // presentBillingItemRow()
